import type { Archetype, ArchetypeKey } from '../types/assessment'

/**
 * The eight archetypes, their readouts, and their card colors.
 * Names and scoring keys must not change without changing data/questions.ts.
 *
 * To swap the emoji for illustrated artwork, replace `emoji` with an image URL
 * and render an <img> in components/results/ArchetypeGlyph.tsx.
 */
export const ARCHETYPES: Record<ArchetypeKey, Archetype> = {
  Puppy: {
    key: 'Puppy',
    name: 'Puppy',
    emoji: '🐶',
    core: 'Reaches toward connection when the Drop fires.',
    vulnerable: 'You reach because you believe connection is worth the risk.',
    strength: 'You are often the reason people feel found.',
    matches: 'Cat',
    desc: 'You monitor relational temperature closely and tend to accommodate before being asked to. When something shifts in a relationship, you feel it before you can name it, and your instinct is to move toward the person. The work is learning that connection is real even when it is not actively being confirmed.',
    theme: { bg1: '#2A1505', bg2: '#5C2E0A', accent: '#F2B84B', text: '#FDF3E0' },
  },
  Cat: {
    key: 'Cat',
    name: 'Cat',
    emoji: '🐱',
    core: 'Manages exposure through evaluation and strategic distance.',
    vulnerable: 'The distance is protection, not indifference.',
    strength: 'When you choose someone, the loyalty is real and lasting.',
    matches: 'Puppy',
    desc: 'You want connection deeply, but you have learned that full investment without protection is costly. You regulate through distance, not indifference, and you return when you have reestablished yourself internally. The work is letting yourself be seen without losing your sense of self.',
    theme: { bg1: '#0C1422', bg2: '#1A2A44', accent: '#7EB8D4', text: '#E4EEF5' },
  },
  Sheepdog: {
    key: 'Sheepdog',
    name: 'Sheepdog',
    emoji: '🐕',
    core: 'Routes the Drop into protective action.',
    vulnerable: 'The care is genuine. So is the fear of not being needed.',
    strength: 'You create safety for people before they know they need it.',
    matches: 'Hawk',
    desc: 'You feel most settled when useful to someone you care about. Your relational radar stays on, and when someone is struggling, you move toward them before they ask. The warmth you bring is real. The work is distinguishing genuine care from the anxiety underneath it.',
    theme: { bg1: '#091A0D', bg2: '#143A1C', accent: '#72C47A', text: '#E5F2E7' },
  },
  Hawk: {
    key: 'Hawk',
    name: 'Hawk',
    emoji: '🦅',
    core: 'Routes the Drop outward into defense or dominance.',
    vulnerable: 'The longing beneath the directness is to be met as an equal.',
    strength: 'Your clarity, offered without pressure, is rare.',
    matches: 'Horse',
    desc: 'The shame signal converts to anger fast enough that the shame itself is rarely visible, even to you. You value directness, expect reciprocity, and have little patience for what feels like performance. You are not cold; you are clear. The work is letting others arrive at clarity at their own pace.',
    theme: { bg1: '#1A0808', bg2: '#3A1010', accent: '#D4884A', text: '#F5EBE3' },
  },
  Horse: {
    key: 'Horse',
    name: 'Horse',
    emoji: '🐴',
    core: 'Runs at high amplitude in both directions.',
    vulnerable: 'The amplitude is feeling at full volume, not instability.',
    strength: 'When you are fully present, people feel it completely.',
    matches: 'Hawk',
    desc: 'You cycle between full investment and crash, carrying genuine depth of feeling. You are capable of deep attachment, but you need room to feel like yourself. Constraint registers quickly and you move away from it before you can say why. The work is communicating your need for space in ways others can receive.',
    theme: { bg1: '#1C0E04', bg2: '#3D2005', accent: '#D4855A', text: '#F5EBE0' },
  },
  SeaTurtle: {
    key: 'SeaTurtle',
    name: 'Sea Turtle',
    emoji: '🐢',
    core: 'Manages the Drop through preemptive distance.',
    vulnerable: 'The space protects you, and sometimes holds people further than you meant.',
    strength: 'What you offer, when you offer it, holds.',
    matches: 'Puppy',
    desc: 'You sense exposure coming and create space before it arrives. You are not withdrawn; you are unhurried. When you do open up, the connection you offer is real and lasting. External pressure to speed up usually has the opposite effect. The work is helping others understand your pace as a relational style, not a flaw.',
    theme: { bg1: '#051A18', bg2: '#0A3835', accent: '#4EC9C0', text: '#E0F3F2' },
  },
  Hummingbird: {
    key: 'Hummingbird',
    name: 'Hummingbird',
    emoji: '🐦',
    core: 'Moves fast enough to outrun the Drop.',
    vulnerable: 'Staying does not have to mean losing yourself.',
    strength: 'You bring aliveness that genuinely lights people up.',
    matches: 'Sheepdog',
    desc: 'You generate new interest before the shame signal consolidates. New ideas, new people, new possibilities light you up and your energy is genuinely compelling. But it can shift before others have caught up. The work is developing the capacity to stay, even when the initial charge has faded.',
    theme: { bg1: '#14091E', bg2: '#2C1245', accent: '#C47ACA', text: '#F0E5F5' },
  },
  Hedgehog: {
    key: 'Hedgehog',
    name: 'Hedgehog',
    emoji: '🦔',
    core: 'Manages the Drop through containment.',
    vulnerable: 'You feel more than most people around you will ever know.',
    strength: 'When you let someone in, what they receive is the real thing.',
    matches: 'Puppy',
    desc: 'Rich interior, measured surface. You feel things at depth and show very little. You have built your containment carefully because you know what it costs to be exposed before it is safe. When someone earns your trust, the connection you offer is genuine and durable. The work is letting the shell come down incrementally.',
    theme: { bg1: '#090D1C', bg2: '#141A35', accent: '#8A9EC4', text: '#E3E7F5' },
  },
}

export const ARCHETYPE_KEYS = Object.keys(ARCHETYPES) as ArchetypeKey[]

/**
 * Maximum attainable raw score per archetype, used to convert raw scores into
 * the "% match" figure. Derived from the question weights; do not edit by hand.
 */
export const MAX_SCORES: Record<ArchetypeKey, number> = {
  Puppy: 64,
  Cat: 44,
  Sheepdog: 56,
  Hawk: 36,
  Horse: 32,
  SeaTurtle: 32,
  Hummingbird: 44,
  Hedgehog: 52,
}
